import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { AlertCircle, TrendingUp } from 'lucide-react';
import { ReportHeader } from '../components/ReportHeader';
import { Sparkline } from '../components/Sparkline';
import { trailingMonths } from '../lib/snapshotRanges';

const API = process.env.REACT_APP_BACKEND_URL;

const UNAVAILABLE_MSG =
  'Deal Ready Accelerator data is temporarily unavailable. This can happen if your account is not connected to the upstream LevelUp API. Please try again shortly or contact your administrator.';

function fmt(n) {
  return (n ?? 0).toLocaleString();
}

function SABadge({ name }) {
  if (!name) return <span style={{ color: '#94a3b8' }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#4338ca', whiteSpace: 'nowrap' }}>
      {name}
    </span>
  );
}

function PaginationBar({ page, totalPages, total, perPage, onPage, testId }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return (
    <div className="pagination" data-testid={testId}>
      <span className="pagination-info">{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div data-testid="deal-ready-error" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '24px 20px' }}>
      <AlertCircle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
      <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{message}</p>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
    </div>
  );
}

// ── Executive snapshot bar (above tabs) ─────────────────────────────────────────
function SnapshotBar({ dateFrom, dateTo }) {
  const [snap, setSnap] = useState(null);
  const [series, setSeries] = useState(null);
  const reqId = useRef(0);

  // Tile values follow the page FY range.
  useEffect(() => {
    let cancelled = false;
    const myId = ++reqId.current;
    (async () => {
      try {
        const dp = {};
        if (dateFrom) dp.date_from = dateFrom;
        if (dateTo) dp.date_to = dateTo;
        const [launches, byUser] = await Promise.all([
          axios.get(`${API}/api/lup/v2/report/dra-launches`, { params: dp, withCredentials: true }),
          axios.get(`${API}/api/lup/v2/report/dra-launches-by-user`, { params: { page: 1, per_page: 1, ...dp }, withCredentials: true }),
        ]);
        if (cancelled || myId !== reqId.current) return;
        const topDemo = (launches.data?.data || [])[0] || null;
        const topUser = (byUser.data?.data || [])[0] || null;
        setSnap({
          launches: launches.data?.total_launches || 0,
          demos: launches.data?.total_demos || 0,
          users: byUser.data?.total_users || 0,
          topDemo, topUser,
        });
      } catch (e) {
        if (!cancelled && myId === reqId.current) setSnap(null); // hidden gracefully when upstream unavailable
      }
    })();
    return () => { cancelled = true; };
  }, [dateFrom, dateTo]);

  // Period-over-period trend — last 6 calendar months (fixed, mount-only).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const months = trailingMonths(6);
        const res = await Promise.all(months.map(m =>
          axios.get(`${API}/api/lup/v2/report/dra-launches`, { params: { date_from: m.from, date_to: m.to }, withCredentials: true })
        ));
        if (cancelled) return;
        setSeries({ launches: res.map(r => r.data?.total_launches || 0) });
      } catch (e) {
        if (!cancelled) setSeries(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const userLabel = (u) => {
    if (!u) return '—';
    const n = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    return n || (u.student_user_id != null ? `LMS ${u.student_user_id}` : '—');
  };

  const BigTile = ({ k, label, value, sub, spark }) => (
    <div className="stat-card" data-testid={`dra-snapshot-${k}`} style={{ padding: 16, minWidth: 0 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', fontWeight: 600 }}>{label}</div>
      <div className="stat-value" style={{ fontSize: value && value.length > 14 ? 16 : 22, fontWeight: 800, color: '#0f172a', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={value}>{value}</div>
      {sub != null && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{sub}</div>}
      {spark && <Sparkline values={spark} />}
    </div>
  );

  return (
    <div data-testid="dra-snapshot-bar" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.05em', color: '#94a3b8', fontWeight: 700 }}>Deal Ready Snapshot</div>
        {series && <div style={{ fontSize: 11, color: '#94a3b8' }}>Trend: last 6 months</div>}
      </div>
      {!snap ? (
        <div className="stat-card" style={{ padding: 16, color: '#94a3b8', fontSize: 13 }}>Snapshot data unavailable for the selected range.</div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 12 }}>
        <BigTile k="launches" label="Total Launches" value={fmt(snap.launches)} sub={`across ${fmt(snap.demos)} demos`} spark={series?.launches} />
        <BigTile k="demos" label="Demos" value={fmt(snap.demos)} sub="accelerators tracked" />
        <BigTile k="users" label="Unique Users" value={fmt(snap.users)} sub="launched a demo" />
        <BigTile k="top-demo" label="Top Demo" value={snap.topDemo?.demo_name || '—'} sub={snap.topDemo ? `${fmt(snap.topDemo.launches)} launches` : null} />
        <BigTile k="top-user" label="Top User" value={userLabel(snap.topUser)} sub={snap.topUser ? `${fmt(snap.topUser.launches)} launches` : null} />
      </div>
      )}
    </div>
  );
}

// ── Tab 1: DRA Launches (per demo) ────────────────────────────────────────────
function LaunchesTab({ search, solutionArea, dateFrom, dateTo, onSolutionAreas }) {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_demos: 0, total_launches: 0 });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (solutionArea) params.solution_area = solutionArea;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/api/lup/v2/report/dra-launches`, { params, withCredentials: true });
      const data = r.data?.data || [];
      setRows(data);
      setTotals({ total_demos: r.data?.total_demos || 0, total_launches: r.data?.total_launches || 0 });
      if (onSolutionAreas && !solutionArea) {
        const sas = [...new Set(data.map(d => d.solution_area).filter(Boolean))].sort();
        onSolutionAreas(sas);
      }
    } catch (e) {
      setErrorMsg(UNAVAILABLE_MSG);
      setRows([]);
      setTotals({ total_demos: 0, total_launches: 0 });
    } finally {
      setLoading(false);
    }
  }, [search, solutionArea, dateFrom, dateTo, onSolutionAreas]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="table-container" data-testid="dra-launches-tab">
      {loading ? <Spinner /> : errorMsg ? <ErrorBanner message={errorMsg} /> : rows.length === 0 ? (
        <div className="empty-state"><h3>No demos found</h3><p>Try adjusting your filters.</p></div>
      ) : (
        <>
          <div className="table-header">
            <div>
              <span className="table-title">DRA Launches</span>
              <span className="table-count" style={{ marginLeft: 8 }}>
                ({fmt(totals.total_demos)} demos · {fmt(totals.total_launches)} launches)
              </span>
            </div>
          </div>
          <div className="table-scroll">
            <table data-testid="dra-launches-table">
              <thead>
                <tr>
                  <th>Demo Name</th>
                  <th>Solution Area</th>
                  <th className="td-right">Launches</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.accelerator_id} data-testid={`dra-launch-row-${row.accelerator_id}`}>
                    <td style={{ fontSize: 13, fontWeight: 500 }}>{row.demo_name || '—'}</td>
                    <td><SABadge name={row.solution_area} /></td>
                    <td className="td-right" style={{ fontWeight: 700 }}>{fmt(row.launches)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab 2: DRA Launches by User ───────────────────────────────────────────────
function ByUserTab({ search, solutionArea, dateFrom, dateTo }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState({ total_demos: 0, total_users: 0, total_launches: 0 });
  const [piiAllowed, setPiiAllowed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const PER_PAGE = 25;

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setPiiAllowed(r.data?.pii_allowed === true))
      .catch(() => setPiiAllowed(false));
  }, []);

  const fetchData = useCallback(async (p) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const params = { page: p, per_page: PER_PAGE };
      if (search) params.search = search;
      if (solutionArea) params.solution_area = solutionArea;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await axios.get(`${API}/api/lup/v2/report/dra-launches-by-user`, { params, withCredentials: true });
      setRows(r.data?.data || []);
      setTotal(r.data?.total || 0);
      setTotalPages(r.data?.last_page || 1);
      setPage(r.data?.current_page || p);
      setSummary({
        total_demos: r.data?.total_demos || 0,
        total_users: r.data?.total_users || 0,
        total_launches: r.data?.total_launches || 0,
      });
    } catch (e) {
      setErrorMsg(UNAVAILABLE_MSG);
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [search, solutionArea, dateFrom, dateTo]);

  useEffect(() => { fetchData(1); }, [fetchData]);

  const userName = (row) => {
    const n = `${row.first_name || ''} ${row.last_name || ''}`.trim();
    return n || '—';
  };

  return (
    <div className="table-container" data-testid="dra-by-user-tab">
      {loading ? <Spinner /> : errorMsg ? <ErrorBanner message={errorMsg} /> : rows.length === 0 ? (
        <div className="empty-state"><h3>No launches found</h3><p>Try adjusting your filters.</p></div>
      ) : (
        <>
          <div className="table-header">
            <div>
              <span className="table-title">DRA Launches by User</span>
              <span className="table-count" style={{ marginLeft: 8 }}>
                ({fmt(summary.total_users)} users · {fmt(summary.total_launches)} launches)
              </span>
            </div>
          </div>
          <div className="table-scroll">
            <table data-testid="dra-by-user-table">
              <thead>
                <tr>
                  <th>Demo Name</th>
                  <th>Solution Area</th>
                  {piiAllowed && <th>User</th>}
                  <th>LMS ID</th>
                  {piiAllowed && <th>Email</th>}
                  <th>Country</th>
                  <th>Region</th>
                  <th className="td-right">Launches</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={`${row.accelerator_id}-${row.student_user_id}-${i}`} data-testid={`dra-user-row-${i}`}>
                    <td style={{ fontSize: 13 }}>{row.demo_name || '—'}</td>
                    <td><SABadge name={row.solution_area} /></td>
                    {piiAllowed && <td style={{ fontSize: 13 }}>{userName(row)}</td>}
                    <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#475569' }}>{row.student_user_id ?? '—'}</td>
                    {piiAllowed && <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#3182ce' }}>{row.email || '—'}</td>}
                    <td style={{ fontSize: 12 }}>{row.country || '—'}</td>
                    <td style={{ fontSize: 12 }}>{row.region || '—'}</td>
                    <td className="td-right" style={{ fontWeight: 700 }}>{fmt(row.launches)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationBar page={page} totalPages={totalPages} total={total} perPage={PER_PAGE}
            onPage={p => fetchData(p)} testId="dra-by-user-pagination" />
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function InsightsCubeDealReady() {
  const [tab, setTab] = useState('launches');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [solutionArea, setSolutionArea] = useState('');
  const [period, setPeriod] = useState({ startDate: '', endDate: '' });
  const [solutionAreas, setSolutionAreas] = useState([]);

  const handlePeriod = useCallback(({ startDate, endDate }) => setPeriod({ startDate: startDate || '', endDate: endDate || '' }), []);
  const handleApply = () => setSearch(searchInput);
  const handleReset = () => {
    setSearchInput(''); setSearch('');
    setSolutionArea('');
  };

  const onSolutionAreas = useCallback((sas) => setSolutionAreas(sas), []);

  return (
    <div data-testid="insights-deal-ready-page">
      <ReportHeader reportTitle="Deal Ready for PSH" onChange={handlePeriod} />
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <TrendingUp size={22} color="#4338ca" /> Deal Ready for PSH
        </h1>
        <p>Deal Ready Accelerator (DRA) usage feed delivered to EAS Partner Skilling Hub (PSH) — demo launches overall and per user, sourced live from the LevelUp reporting API.</p>
      </div>

      <SnapshotBar dateFrom={period.startDate} dateTo={period.endDate} />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e2e8f0', margin: '0 0 16px' }}>
        {[
          { key: 'launches', label: 'DRA Launches' },
          { key: 'by-user', label: 'DRA Launches by User' },
        ].map(t => (
          <button
            key={t.key}
            data-testid={`deal-ready-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              color: tab === t.key ? '#4338ca' : '#64748b',
              borderBottom: tab === t.key ? '2px solid #4338ca' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input type="text" className="filter-input" placeholder={tab === 'by-user' ? 'Demo, user, email, partner…' : 'Demo name…'}
            value={searchInput} onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleApply()} data-testid="deal-ready-search" />
        </div>
        <div className="filter-group">
          <label className="filter-label">Solution Area</label>
          <select className="filter-select" value={solutionArea} onChange={e => setSolutionArea(e.target.value)} data-testid="deal-ready-solution-area">
            <option value="">All</option>
            {solutionAreas.map(sa => <option key={sa} value={sa}>{sa}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={handleApply} data-testid="deal-ready-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={handleReset} data-testid="deal-ready-reset-btn">Reset</button>
        </div>
      </div>

      {tab === 'launches' ? (
        <LaunchesTab search={search} solutionArea={solutionArea} dateFrom={period.startDate} dateTo={period.endDate} onSolutionAreas={onSolutionAreas} />
      ) : (
        <ByUserTab search={search} solutionArea={solutionArea} dateFrom={period.startDate} dateTo={period.endDate} />
      )}
    </div>
  );
}
